/**
 * 🐾 Fissal Prime Device Registry & Auto-Detection Engine
 * 
 * Maintains a living registry of physical & wireless devices across
 * The Relay, mobile OTG ports, and the Tailscale mesh.
 * Provides non-intrusive auto-detection notifications and primes Fissal AI
 * with exact hardware parameters for instant co-piloting.
 */

export type DeviceTransport = 'usb' | 'wifi' | 'bluetooth' | 'native';

export type DeviceKind = 
  | 'sound_console' 
  | 'lighting_rig' 
  | 'serial_logic' 
  | 'camera_ptz' 
  | 'smart_device' 
  | 'sensor_node';

export interface RegisteredDevice {
  id: string;
  name: string;
  kind: DeviceKind;
  transport: DeviceTransport;
  status: 'online' | 'standby' | 'disconnected' | 'probing';
  details: string;
  deckId: 'sound' | 'lighting' | 'camera' | 'serial' | 'diagnostics';
  lastSeen: number;
  capabilities: string[];
  telemetry?: Record<string, any>;
}

export type DeviceDetectListener = (device: RegisteredDevice) => void;

class DeviceRegistry {
  private devices: Map<string, RegisteredDevice> = new Map();
  private listeners: Set<DeviceDetectListener> = new Set();

  constructor() {
    this.seedDefaultEcosystem();
  }

  private seedDefaultEcosystem() {
    const defaults: RegisteredDevice[] = [
      {
        id: 'x32-console',
        name: 'Behringer X32 Sound Console',
        kind: 'sound_console',
        transport: 'wifi',
        status: 'online',
        details: 'UDP 10023 OSC • 4 DCA Groups • RTA Soundcheck',
        deckId: 'sound',
        lastSeen: Date.now(),
        capabilities: ['osc_control', 'faders', 'mutes', 'soundcheck_synth'],
        telemetry: { mainFader: 0.75, heartbeat: 'active', ip: '192.168.68.50' }
      },
      {
        id: 'stage-lighting-dimmer',
        name: 'Guardian Stage Lighting Dimmer Rack',
        kind: 'lighting_rig',
        transport: 'usb',
        status: 'online',
        details: 'Enttec Pro USB DMX / ArtNet • 4 Altman Lekos, Pars, Strobes',
        deckId: 'lighting',
        lastSeen: Date.now(),
        capabilities: ['dmx512', 'grand_master', 'guarded_dbo', 'tungsten_ramp'],
        telemetry: { grandMaster: 100, fixturesCount: 7, blackout: false }
      },
      {
        id: 'camera-eroto1',
        name: 'Dogshop Porch PTZ (Tapo TCW30)',
        kind: 'camera_ptz',
        transport: 'wifi',
        status: 'online',
        details: '192.168.70.103 • Full ONVIF PTZ, Light, Speaker',
        deckId: 'camera',
        lastSeen: Date.now(),
        capabilities: ['ptz_dpad', 'presets', 'spotlight', 'rtsp_substream'],
        telemetry: { ip: '192.168.70.103', fps: 3.1, ptzReady: true }
      },
      {
        id: 'camera-ipcam100',
        name: 'Lycan Front Road & Driveway',
        kind: 'camera_ptz',
        transport: 'wifi',
        status: 'online',
        details: '192.168.70.100 • Perimeter Motion Detect & Recording',
        deckId: 'camera',
        lastSeen: Date.now(),
        capabilities: ['rtsp_stream', 'motion_detect'],
        telemetry: { ip: '192.168.70.100', fps: 4.1 }
      },
      {
        id: 'camera-lycanyard',
        name: 'Lycan Yard Towards Echo',
        kind: 'camera_ptz',
        transport: 'wifi',
        status: 'online',
        details: '192.168.70.101 • Hi3510 Hipcam • Perimeter Zone',
        deckId: 'camera',
        lastSeen: Date.now(),
        capabilities: ['rtsp_stream', 'motion_detect'],
        telemetry: { ip: '192.168.70.101', fps: 4.1 }
      },
      {
        id: 'camera-backporch',
        name: "Sharon's Back Porch (Tapo TCW61)",
        kind: 'camera_ptz',
        transport: 'wifi',
        status: 'online',
        details: '192.168.70.102 • Audio & Spotlight • Dog Safety',
        deckId: 'camera',
        lastSeen: Date.now(),
        capabilities: ['rtsp_stream', 'audio_listen', 'spotlight'],
        telemetry: { ip: '192.168.70.102', fps: 3.0 }
      },
      {
        id: 'usb-serial-otg',
        name: 'USB-C Serial Microcontroller Rig',
        kind: 'serial_logic',
        transport: 'usb',
        status: 'standby',
        details: 'CP2102 / FTDI / CH340 • 115200 8N1 • DTR/RTS Ready',
        deckId: 'serial',
        lastSeen: Date.now(),
        capabilities: ['baud_select', 'loopback_test', 'hex_ascii_monitor', 'at_commands'],
        telemetry: { baudRate: 115200, driver: 'CP2102', vid: '0x10C4', pid: '0xEA60' }
      },
      {
        id: 'pixel-11-pro-xl',
        name: 'Google Pixel 11 Pro XL (Echo Handheld)',
        kind: 'sensor_node',
        transport: 'wifi',
        status: 'online',
        details: 'Tailscale Mesh 100.68.209.76 • Tensor G4 Edge Silicon',
        deckId: 'diagnostics',
        lastSeen: Date.now(),
        capabilities: ['tailscale_mesh', 'bastet_native', 'gemma_litert', 'shizuku_rootless'],
        telemetry: { ip: '100.68.209.76', tailscaleOnline: true }
      },
      {
        id: 'bastet-sensors',
        name: 'Bastet Native Sensor Suite',
        kind: 'sensor_node',
        transport: 'native',
        status: 'online',
        details: 'Magnetometer EMF • Battery Metrology • Lux • NFC',
        deckId: 'diagnostics',
        lastSeen: Date.now(),
        capabilities: ['emf_survey', 'battery_thermals', 'nfc_read', 'lux_meter'],
        telemetry: { emfU_T: 42.8, batteryTemp: 38.2, batteryHealth: 'Good' }
      }
    ];

    defaults.forEach(d => this.devices.set(d.id, d));
  }

  getAllDevices(): RegisteredDevice[] {
    return Array.from(this.devices.values());
  }

  getDevicesByTransport(transport: DeviceTransport): RegisteredDevice[] {
    return this.getAllDevices().filter(d => d.transport === transport);
  }

  getDevice(id: string): RegisteredDevice | undefined {
    return this.devices.get(id);
  }

  registerOrUpdateDevice(device: RegisteredDevice) {
    const isNew = !this.devices.has(device.id);
    this.devices.set(device.id, { ...device, lastSeen: Date.now() });

    if (isNew || device.status === 'online') {
      this.notifyListeners(device);
    }
  }

  onDeviceDetected(callback: DeviceDetectListener): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(device: RegisteredDevice) {
    this.listeners.forEach(cb => {
      try {
        cb(device);
      } catch (err) {
        console.error('[DeviceRegistry] Error in listener callback:', err);
      }
    });
  }

  /**
   * Helper to trigger a realistic detection event for testing or simulation
   */
  triggerMockDetection(type: 'usb_serial' | 'x32_osc' | 'nfc_fixture' | 'ble_beacon') {
    let mockDevice: RegisteredDevice;
    switch (type) {
      case 'usb_serial':
        mockDevice = {
          id: `usb-${Date.now().toString(36)}`,
          name: 'FTDI USB-UART Serial Adapter',
          kind: 'serial_logic',
          transport: 'usb',
          status: 'online',
          details: 'VID: 0x0403 | PID: 0x6001 • FTDI FT232R @ 115200 baud',
          deckId: 'serial',
          lastSeen: Date.now(),
          capabilities: ['baud_select', 'loopback_test', 'hex_ascii_monitor'],
          telemetry: { baudRate: 115200, driver: 'FTDI', vid: '0x0403', pid: '0x6001' }
        };
        break;
      case 'x32_osc':
        mockDevice = {
          id: 'x32-console',
          name: 'Behringer X32 Sound Console',
          kind: 'sound_console',
          transport: 'wifi',
          status: 'online',
          details: 'Discovered on UDP port 10023 • Live /xremote response verified',
          deckId: 'sound',
          lastSeen: Date.now(),
          capabilities: ['osc_control', 'faders', 'mutes', 'soundcheck_synth'],
          telemetry: { ip: '192.168.68.50', status: 'handshake_ok' }
        };
        break;
      case 'nfc_fixture':
        mockDevice = {
          id: `nfc-${Date.now().toString(36)}`,
          name: 'Altman Leko 360Q #1 Tag',
          kind: 'lighting_rig',
          transport: 'native',
          status: 'online',
          details: 'NFC UID 04:A1:B2:C3 • 500W Tungsten Fixture @ DMX Ch 1',
          deckId: 'lighting',
          lastSeen: Date.now(),
          capabilities: ['dmx512', 'grand_master'],
          telemetry: { channel: 1, watts: 500, type: 'altman_360q_500w' }
        };
        break;
      case 'ble_beacon':
        mockDevice = {
          id: `ble-${Date.now().toString(36)}`,
          name: 'The Relay Living Room Beacon',
          kind: 'sensor_node',
          transport: 'bluetooth',
          status: 'online',
          details: 'iBeacon UUID: f155a1... • RSSI: -48 dBm (~1.2m proximity)',
          deckId: 'diagnostics',
          lastSeen: Date.now(),
          capabilities: ['proximity_radar', 'ble_rssi'],
          telemetry: { rssi: -48, estimatedDistanceMeters: 1.2 }
        };
        break;
    }
    this.registerOrUpdateDevice(mockDevice);
  }
}

export const deviceRegistry = new DeviceRegistry();
